import { z } from 'zod'
import { BillingCode } from 'src/participantHistory/intakeNote/billingCodeConstants'

const MAX_INPUT_LENGTH = 3000

export const MONTHLY_SUMMARY_NOTE_FORM_SCHEMA = z.object({
  date: z.string(),
  participant: z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    memberID: z.string().min(1, 'This is required'),
  }),
  assessingPractitioner: z.object({
    name: z.string().min(1, 'Name is required'),
    licenseType: z.string().min(1, 'License type is required'),
  }),
  cptCode: z.string().optional(),
  datesOfService: z.object({
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
  }),
  treatmentPlan: z.object({
    smartGoals: z.string().max(MAX_INPUT_LENGTH),
  }),
  billingCodes: z.array(z.nativeEnum(BillingCode)).min(1, 'At least one billing code is required'),
  interventions: z.object({
    PHQ9_GAD7: z.boolean(),
    BIOFEEDBACK: z.boolean(),
    IN_APP_CHAT: z.boolean(),
    CBT_MINDFULLNESS_ACT: z.boolean(),
    PSYCHOEDUCATION: z.boolean(),
    TELEHEALTH_VIDEO: z.boolean(),
    PEER_SUPPORT: z.boolean(),
  }),
  reasonForSeekingTreatment: z.object({
    functionalImpairments: z
      .object({
        DIFFICULTY_MEETING_EXPECTATIONS: z.boolean(),
        STRUGGLE_WITH_RELATIONSHIPS: z.boolean(),
        NEGLECT_OF_PERSONAL_NEEDS: z.boolean(),
        DIFFICULTY_IN_SLEEP: z.boolean(),
        CHANGE_IN_APPETITE_OR_WEIGHT: z.boolean(),
        IMPAIRED_COGNITIVE_FUNCTIONING: z.boolean(),
        VOLATILE_MOOD_RESPONSES: z.boolean(),
        DIFFICULTY_MANAGING_TASKS: z.boolean(),
        LOSS_OF_INTEREST: z.boolean(),
        OTHER: z.string().max(MAX_INPUT_LENGTH).optional(),
      })
      .superRefine((data, ctx) => {
        const { OTHER, ...checkboxes } = data
        const hasCheckedImpairment = Object.values(checkboxes).some(Boolean)
        const hasOtherText = Boolean(OTHER?.trim())
        if (!hasCheckedImpairment && !hasOtherText) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please select at least one functional impairment from the list or write one that is not listed.',
          })
        }
      }),
  }),
  participantEngagement: z.object({
    participation: z.string().max(MAX_INPUT_LENGTH),
    chatSummary: z.string().trim().min(1, 'Chat summary is required').max(MAX_INPUT_LENGTH),
  }),
  progressAssessment: z.object({
    sinceLastAssessment: z.object({
      healthQuestionnaire: z.object({
        PHQ9: z.object({
          QUESTION1: z.number(),
          QUESTION2: z.number(),
          QUESTION3: z.number(),
          QUESTION4: z.number(),
          QUESTION5: z.number(),
          QUESTION6: z.number(),
          QUESTION7: z.number(),
          QUESTION8: z.number(),
          QUESTION9: z.number(),
          TOTAL_SCORE: z.number(),
        }),
        GAD7: z.object({
          QUESTION1: z.number(),
          QUESTION2: z.number(),
          QUESTION3: z.number(),
          QUESTION4: z.number(),
          QUESTION5: z.number(),
          QUESTION6: z.number(),
          QUESTION7: z.number(),
          TOTAL_SCORE: z.number(),
        }),
      }),
      assessment: z
        .object({
          NO_NOTABLE_CHANGE: z.boolean(),
          OVERALL_IMPROVEMENT: z.boolean(),
          OVERALL_WORSENING: z.boolean(),
          FLUCTUATING_PATTERN: z.boolean(),
          NOT_COMPLETED_QUESTIONNAIRES: z.boolean(),
          REMINDERS_SENT: z.boolean(),
          details: z.string().max(MAX_INPUT_LENGTH).optional(),
        })
        .superRefine((data, ctx) => {
          if (data.OVERALL_WORSENING || data.FLUCTUATING_PATTERN) {
            if (!data.details || data.details.trim().length === 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'Details required when "Overall symptom worsening" or "Fluctuating symptom pattern" is selected',
                path: ['details'],
              })
            }
          }
        }),
    }),
    riskAssessment: z
      .object({
        status: z.enum(['noChanges', 'changes']),
        details: z.string().max(MAX_INPUT_LENGTH).optional(),
      })
      .superRefine((data, ctx) => {
        if (data.status === 'changes') {
          if (!data.details || data.details.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Details required when "Changes to risk level" is selected',
              path: ['details'],
            })
          }
        }
      }),
    participantResponse: z
      .object({
        response: z.enum(['activelyEngaged', 'minimallyEngaged', 'other']),
        details: z.string().max(MAX_INPUT_LENGTH).optional(),
      })
      .superRefine((data, ctx) => {
        if (data.response === 'other') {
          if (!data.details || data.details.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Details required when "Other (specify engagement level)" is selected',
              path: ['details'],
            })
          }
        }
      }),
    plan: z
      .object({
        planType: z.enum(['continueSupport', 'other']),
        details: z.string().max(MAX_INPUT_LENGTH).optional(),
      })
      .superRefine((data, ctx) => {
        if (data.planType === 'other') {
          if (!data.details || data.details.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Details required when "Other" is selected',
              path: ['details'],
            })
          }
        }
      }),
  }),
})

export type MonthlySummaryNoteFormType = z.infer<typeof MONTHLY_SUMMARY_NOTE_FORM_SCHEMA>

export const getMonthlySummaryNoteFormSchema = (isKaiser: boolean) =>
  isKaiser
    ? MONTHLY_SUMMARY_NOTE_FORM_SCHEMA.extend({
        cptCode: z.string().min(1, 'CPT code is required'),
      })
    : MONTHLY_SUMMARY_NOTE_FORM_SCHEMA.omit({ cptCode: true })
